/** @format */

import React, { FunctionComponent, useEffect, useState } from 'react';
import SimCardInterface from '../../../interfaces/platform/SimCardInterface';
import GridContainer from '../../../components/Grid/GridContainer';
import { PlkStatusMap } from '../../../helpers/iccidStatus';
import { Auth } from '../../../modules/auth/Auth';
import Card from '../../../components/Card/Card';
import CardBody from '../../../components/Card/CardBody';
import GridItem from '../../../components/Grid/GridItem';
import ChangeStatus from '../../Features/ChangeStatus';
import { SimCardOperationHelper } from '../../../helpers/SimCardOperationHelper';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import SimCardChangeMsisdn from './SimCardChangeMsisdn';
import SimCardChangeIccid from './SimCardChangeIccid';
import { ReloadAccount } from '../../Features/ReloadAccount';
import AdjustmentAccount from '../../Features/AdjustmentAccount';
import { UnitLimit } from '../../Features/UnitLimit';
import { LimitPremium } from '../../Features/LimitPremium';
import { ProductOplChangeApn } from './ProductOplChangeApn';
import { FupSmsMessageOnProduct } from '../../Features/FupSmsMessageOnProduct';
import { ChangeSPN } from '../../Features/ChangeSPN';
import { useDispatch } from 'react-redux';
import PlatformResellerInterface from '../../../interfaces/platform/PlatformResellerInterface';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { configDefault } from '../../../core/config';

type ProductOplViewType = {
    product: SimCardInterface;
    loadProduct: () => void;
};

export const ProductOplView: FunctionComponent<ProductOplViewType> = ({ product, loadProduct }): React.ReactElement => {
    const dispatch = useDispatch();
    const [platformReseller, setPlatformReseller] = useState<PlatformResellerInterface | null>(null);

    useEffect(() => {
        if (platformReseller === null || product.platformResellerId !== platformReseller.id) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getWithCallback(
                FormGeneralHelper.getByUri(
                    FormGeneralHelper.getPath('/platform_resellers/' + product.platformResellerId, configDefault.PLATFORM_API_PREFIX)
                ),
                'no-action',
                (data: PlatformResellerInterface): void => {
                    setPlatformReseller(data);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [platformReseller, product, dispatch]);

    return (
        <GridContainer>
            <GridItem xs={12} md={3} lg={3}>
                {UserRoleHelper.checkRole(roles.ROLE_BLOCK_OPERATION_ON_SERVICES_POST) &&
                    product &&
                    product.mvnoStatus &&
                    (product.mvnoStatus > PlkStatusMap.PREACTIVATED || Auth.isExpertMode()) && (
                        <React.Fragment>
                            <Card>
                                <CardBody>
                                    <FupSmsMessageOnProduct product={product} refreshPage={loadProduct} />
                                    <hr />
                                    <LimitPremium product={product} refreshPage={loadProduct} />
                                    <hr />
                                    <UnitLimit product={product} refreshPage={loadProduct} />
                                </CardBody>
                            </Card>
                        </React.Fragment>
                    )}
            </GridItem>
            {product &&
                (SimCardOperationHelper.isMvnoActive(product) || Auth.isExpertMode()) &&
                (UserRoleHelper.checkRole(roles.ROLE_PLATFORM_CHANGE_MSISDN) || UserRoleHelper.checkRole(roles.ROLE_PLATFORM_CHANGE_ICCID)) && (
                    <React.Fragment>
                        <GridItem xs={12} md={3} lg={3}>
                            <ReloadAccount product={product} refreshPage={loadProduct} />
                            <hr />
                            <AdjustmentAccount simCard={product} refreshPage={loadProduct} />
                            <hr />
                        </GridItem>
                        <GridItem xs={12} md={3} lg={3}>
                            {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_CHANGE_MSISDN) && (
                                <React.Fragment>
                                    <SimCardChangeMsisdn item={product} callback={(): void => {}} />
                                    <hr />
                                </React.Fragment>
                            )}
                            {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_CHANGE_ICCID) && (
                                <SimCardChangeIccid item={product} callback={(): void => {}} />
                            )}
                        </GridItem>
                    </React.Fragment>
                )}
            {(UserRoleHelper.checkRole(roles.ROLE_PLATFORM_OPERATION_ON_SIMCARD_CHANGE_STATUS) ||
                UserRoleHelper.checkRole(roles.ROLE_PLATFORM_OPERATION_ON_SIMCARD_CHANGE_SPN)) &&
                product &&
                product.mvnoStatus &&
                (product.mvnoStatus > PlkStatusMap.PREACTIVATED || Auth.isExpertMode()) && (
                    <React.Fragment>
                        <GridItem xs={12} md={3} lg={3}>
                            <Card>
                                <CardBody>
                                    <GridContainer>
                                        {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_OPERATION_ON_SIMCARD_CHANGE_SPN) && (
                                            <GridItem xs={12}>
                                                <ChangeSPN product={product} refreshPage={loadProduct} />
                                            </GridItem>
                                        )}
                                        {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_OPERATION_ON_SIMCARD_CHANGE_STATUS) && (
                                            <GridItem xs={12}>
                                                <ChangeStatus simCard={product} refreshPage={loadProduct} />
                                                <hr />
                                            </GridItem>
                                        )}
                                        {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_CHANGE_APN) && (
                                            <GridItem xs={12}>
                                                {platformReseller && (
                                                    <ProductOplChangeApn
                                                        product={product}
                                                        refreshPage={loadProduct}
                                                        platformReseller={platformReseller}
                                                    />
                                                )}
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                </CardBody>
                            </Card>
                        </GridItem>
                    </React.Fragment>
                )}
        </GridContainer>
    );
};
